import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  model,
  signal,
  untracked,
  WritableSignal
} from '@angular/core';
import { MatSelectModule } from '@angular/material/select';
import { AppContext } from 'src/app/app/context';
import { DataType } from 'src/app/pbir/dataModel/DataType';
import { PredicateType } from 'src/app/pbir/utils/Predicate';
import { VisualFilter } from 'src/app/pbir/visuals/VisualFilter';
import { RelativeDateOrTimeOperator, RelativeDateOrTimeValuesDto, RelativeDateTimeFilterUnit, TimestampPrecision, VisualService } from 'src/app/services/api.services';
import { isCalendarUnit, PivotStudioDateTimeUnits, UnitKind } from 'src/app/shared/compatibility/filter.models';
import { SharedModule } from 'src/app/shared/shared.module';

@Component({
  selector: 'filter-editor__relativeDateOrTime',
  template: `
    <div class="visual-editor-box__input">
      <mat-select class="trg-outline user-select-none w-100p h-24"
                  panelClass="trg-outline-dropdown"
                  [(ngModel)]="operator"
                  (ngModelChange)="savePredicate()">
        @for (o of operators(); track o.id) {
          <mat-option [value]="o" [title]="o.title">
            <span>{{o.title}}</span>
          </mat-option>
        }
      </mat-select>
    </div>

    <div class="visual-editor-box__input" [hidden]="!showUnit1()">
      <ng-container *ngTemplateOutlet="unitTemplate; context: { unit: unit1 }"></ng-container>
    
      <div class="fs-10 mt-6" [hidden]="!showUnit2()">
        <span>And</span>
      </div>
    </div>

    <div class="visual-editor-box__input" [hidden]="!showUnit2()">
      <ng-container *ngTemplateOutlet="unitTemplate; context: { unit: unit2, future: true }"></ng-container>
    </div>
    
    <div [hidden]="!showIncludeToday()" class="d-flex justify-content-right w-100p pr-4 border-box">
      <trg-checkbox size="tiny" appearance="square"
        [(value)]="includeToday"
        (valueChange)="savePredicate()"
        [hideBorder]="true">
        Include Today
      </trg-checkbox>
    </div>
      
    <div class="d-flex justify-content-center w-100p pr-4 border-box h-16 preview" 
         [class.fade-in]="!previewLoading()">
      <app-relative-date-range [filter]="relativeDateRangeValue()" [stylingInfo]="{ 'font-size': '10px' }"
                               [(loading)]="previewLoading"></app-relative-date-range>
    </div>

    <ng-template #unitTemplate let-unit="unit" let-future="future">
      @let open = future ? openFuture : openPast;
      <mat-select class="trg-outline user-select-none w-100p h-24"
                  panelClass="trg-outline-dropdown"
                  [hidden]="!isClosedRange()"
                  [ngModel]="open()"
                  (ngModelChange)="setIsOpenRange(future, $event)"
                  [class.mb-6]="!open()">
        <mat-option [value]="false">
          <span>In the {{future ? 'next' : 'last'}}</span>
        </mat-option>
        <mat-option [value]="true">
          <span>In the {{future ? 'future' : 'past'}}</span>
        </mat-option>
      </mat-select>
      @if (!open()) {
        <div class="d-flex justify-content-between w-100p">
          <input [hidden]="!showUnitCount()" type="number" min="1"
                 class="trg-outline mr-4 max-w-80" 
                 [(ngModel)]="unit.unitCount" 
                 (ngModelChange)="savePredicate()" />
          <mat-select class="trg-outline user-select-none w-100p h-24"
                      panelClass="trg-outline-dropdown"
                      [(ngModel)]="unit.unitType"
                      (ngModelChange)="savePredicate()"
                      [class.unit-type-no-count]="!showUnitCount()">
            @for (u of units(); track u.id) {
              @let unitTitle = singularUnitTitles() ? u.titleSingular : u.title;
              <mat-option [value]="u.id" [title]="unitTitle">
                <span>{{unitTitle}}</span>
              </mat-option>
            }
          </mat-select>
        </div>
      }
    </ng-template>
  `,
  styles: [`
    :host {
      display: contents;
    }

    ::ng-deep filter-editor__relativeDateOrTime {
      trg-checkbox {
        margin-bottom: 6px;

        .trg-checkbox__text {
          font-size: 11px !important;
          color: #2e2e2e !important;
          position: relative;
          top: -1px;
        }
      }

      app-data-input .filter-input {
        width: 100%;

        .mat-datepicker-toggle i {  
          font-size: 13px !important;
          top: -3px !important;
          left: -1px !important;
        }
      }

      mat-select.unit-type-no-count {
        max-width: calc(100% - 80px);
      }
    }

    .preview {
      transition: opacity 0.2s ease-in-out;
      opacity: 0;

      &.fade-in {
        opacity: 1;
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatSelectModule, SharedModule],
  standalone: true
})
export class RelativeDateOrTimeFilterComponent {
  readonly appContext = inject(AppContext);

  readonly singularUnitTitles = computed(() => this.isExact());
  readonly showUnitCount = computed(() => !this.isExact());

  readonly openPast = computed(() => this.isClosedRange() && this.unit1.unitCount() == 0);
  readonly openFuture = computed(() => this.isClosedRange() && this.unit2.unitCount() == 0);

  readonly showUnit1 = computed(() => true);
  readonly showUnit2 = computed(() => this.isClosedRange());

  readonly showIncludeToday = computed(() =>
    this.isOpenRange() &&
    !this.unit1?.isCalendarUnit() &&
    this.unit1.unitConfig()?.kind == UnitKind.Date);

  readonly includeToday = model(false);

  readonly unit1 = new UnitModel(RelativeDateTimeFilterUnit.Days, 1);
  readonly unit2 = new UnitModel(RelativeDateTimeFilterUnit.Days, 1);

  readonly units = computed(() => {
    let hasDate = this.hasDate();
    let hasTime = this.hasTime();

    return PivotStudioDateTimeUnits.filter(x => x.kind == UnitKind.Date ? hasDate : hasTime);
  });


  readonly operators = computed(() => Operators);
  readonly operator = model<Operator>(Operator.OlderThan);

  readonly isExact = computed(() => this.operator() == Operator.Exact);
  readonly isOpenRange = computed(() => [Operator.InTheLast, Operator.InTheNext].includes(this.operator()));
  readonly isOlderThan = computed(() => this.operator() == Operator.OlderThan);
  readonly isClosedRange = computed(() => this.operator() == Operator.Range);

  readonly target = computed(() => this.predicate()?.target);
  readonly dataType = computed(() => this.target()?.dataType ?? DataType.Unknown);

  readonly hasDate = computed(() => this.dataType().isDateOrDateTime);
  readonly hasTime = computed(() => this.dataType().isTimeOrDateTime);
  readonly showSeconds = computed(() => this.hasTime() && this.target().hasTimestampPrecision(TimestampPrecision.Seconds));


  readonly predicate = model<Partial<VisualFilter.RelativeDatePredicate> & Partial<VisualFilter.RelativeTimePredicate>>({
    operator: RelativeDateOrTimeOperator.InNext,
    firstUnitType: RelativeDateTimeFilterUnit.CalendarMonths,
    firstUnitCount: 1,
    includeToday: false,
  });

  updatePredicate(changes: Partial<VisualFilter.RelativeDatePredicate> & Partial<VisualFilter.RelativeTimePredicate>) {
    let predicate = {
      ...(this.predicate() ?? {}),
      ...changes
    };

    this.predicate.set({
      type: predicate.type,
      target: predicate.target,
      operator: predicate.operator,
      firstUnitCount: predicate.firstUnitCount,
      firstUnitType: predicate.firstUnitType,
      secondUnitCount: predicate.secondUnitCount,
      secondUnitType: predicate.secondUnitType,
      includeToday: predicate.includeToday,
    });
  }

  setIsOpenRange(future: boolean, open: boolean) {
    let unit = future ? this.unit2 : this.unit1;

    if (open) {
      unit.unitCount.set(0);
    }
    else {
      unit.unitCount.set(1);
    }

    this.savePredicate();
  }

  savePredicate() {
    let predicate: Partial<VisualFilter.RelativeDatePredicate> & Partial<VisualFilter.RelativeTimePredicate> = {
      operator: this.operator().id,
    };

    if (this.showUnit1()) {
      predicate = {
        ...predicate,
        firstUnitType: this.unit1.unitType(),
        firstUnitCount: (this.showUnitCount() && !this.openPast()) ? this.unit1.unitCount() : 0
      };
    }

    if (this.showUnit2()) {
      predicate = {
        ...predicate,
        secondUnitType: this.unit2.unitType(),
        secondUnitCount: (this.showUnitCount() && !this.openFuture()) ? this.unit2.unitCount() : 0
      };
    }

    if (this.showIncludeToday()) {
      predicate = {
        ...predicate,
        includeToday: this.includeToday(),
      };
    }

    this.updatePredicate(predicate);
  }


  readonly updateFromPredicate = effect(() => {
    let predicate = this.predicate();
    let hasDate = this.hasDate();
    let hasTime = this.hasTime();

    untracked(() => {
      this.operator.set(Operators.find(x => x.id == predicate?.operator) ?? Operators[0]);

      let defaultUnit = hasTime && !hasDate ? RelativeDateTimeFilterUnit.Hours : RelativeDateTimeFilterUnit.Days;

      this.unit1.unitCount.set(predicate?.firstUnitCount ?? 0);
      this.unit1.unitType.set(predicate?.firstUnitType ?? defaultUnit);

      this.unit2.unitCount.set(predicate?.secondUnitCount ?? 0);
      this.unit2.unitType.set(predicate?.secondUnitType ?? defaultUnit);

      this.includeToday.set(predicate?.includeToday);
    });
  });


  readonly visualService = inject(VisualService);

  readonly relativeDateRangeValue = computed<RelativeDateOrTimeValuesDto>(() => {
    let predicate = this.predicate();
    if (!predicate)
      return null;

    return {
      operator: predicate.operator,
      unitType: predicate.firstUnitType,
      unitsCount: predicate.firstUnitCount,
      unit2Type: predicate.secondUnitType,
      units2Count: predicate.secondUnitCount,
      includeToday: !!(predicate as any).includeToday,
    };
  });

  readonly previewLoading = model(false);
}

class Operator {
  constructor(
    readonly id: RelativeDateOrTimeOperator,
    readonly title: string) { }
}

namespace Operator {
  export const
    Exact = new Operator(RelativeDateOrTimeOperator.InThis, 'In this'),
    InTheLast = new Operator(RelativeDateOrTimeOperator.InLast, 'In the last'),
    InTheNext = new Operator(RelativeDateOrTimeOperator.InNext, 'In the next'),
    OlderThan = new Operator(RelativeDateOrTimeOperator.OlderThan, 'Is older than'),
    Range = new Operator(RelativeDateOrTimeOperator.InRange, 'Range');
}

const Operators = [
  Operator.OlderThan,
  Operator.InTheLast,
  Operator.InTheNext,
  Operator.Exact,
  Operator.Range,
];

class UnitModel {
  readonly unitType: WritableSignal<RelativeDateTimeFilterUnit | null>;
  readonly unitCount: WritableSignal<number | null>;
  readonly unitConfig = computed(() => PivotStudioDateTimeUnits.find(x => x.id == this.unitType()));
  readonly isCalendarUnit = computed(() => isCalendarUnit(this.unitType()));

  constructor(unitType?: RelativeDateTimeFilterUnit, unitCount?: number) {
    this.unitType = signal(unitType);
    this.unitCount = signal(unitCount ?? 1);
  }
}
